// src/store/slices/rankingSlice.js
/**
 * rankingSlice & fetchRanking thunk
 * --------------------------------------------------------------------
 * 목적:
 *  - 랭킹 데이터(일/주/월)와 비교 기간 데이터, 기준 날짜, 매장 ID, 로딩/에러 상태를 관리.
 *  - fetchRanking: 주어진 localDate를 기준으로 현재 기간 + 비교 기간(일/주/월)을
 *    병렬 호출(Promise.all)하여 스토어에 저장.
 *
 * 비교 기간 규칙(비즈니스 로직):
 *  - daily   비교: 7일 전(localDate - 7일)
 *  - weekly  비교: 28일 전(localDate - 28일)  → "전주" 1주치 비교를 위한 단순 오프셋 가정
 *  - monthly 비교: 3개월 전(localDate - 3개월)
 *
 * 반환 payload 구조:
 *  {
 *    date: localDate 문자열,
 *    store_id: number | string,
 *    rankDetail: {
 *      daily:   Array<랭킹행>,
 *      weekly:  Array<랭킹행>,
 *      monthly: Array<랭킹행>,
 *    },
 *    lastDetail: {
 *      daily:   Array<랭킹행>, // 비교용
 *      weekly:  Array<랭킹행>,
 *      monthly: Array<랭킹행>,
 *    }
 *  }
 *
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { mokiApi } from "../../services/loginApi";
import { formatDate } from "../../functions/DateConverter";

// 비동기 thunk: 날짜별 랭킹(현재/비교) 6개 API를 병렬 호출
export const fetchRanking = createAsyncThunk(
  "ranking/fetchRanking",
  async (localDate) => {
    // 1) 토큰을 읽어 Authorization 헤더 세팅(있을 때만)
    const token = sessionStorage.getItem("accessToken");
    if (token) {
      mokiApi.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }

    // 2) 비교 기준일 계산: daily/weekly/monthly 각각의 비교일을 만듦
    const dailyCompareDate = new Date(localDate); // 기준일 복제
    const weeklyCompareDate = new Date(localDate);
    const monthlyCompareDate = new Date(localDate);

    // - 일간: 7일 전
    dailyCompareDate.setDate(dailyCompareDate.getDate() - 7);
    // - 주간: 28일 전(= 4주)
    weeklyCompareDate.setDate(weeklyCompareDate.getDate() - 28);
    // - 월간: 3개월 전
    monthlyCompareDate.setDate(monthlyCompareDate.getDate() - 90);

    // 3) 서버가 요구하는 포맷으로 문자열 변환
    const formatDailyCompDate = formatDate(dailyCompareDate);
    const formatWeeklyCompDate = formatDate(weeklyCompareDate);
    const formatMonthlyCompDate = formatDate(monthlyCompareDate);

    // 4) 리듀서로 넘길 최종 payload 초기값(네트워크 실패 대비 기본형)
    let resData = {
      date: localDate,
      store_id: null,
      rankDetail: { daily: null, weekly: null, monthly: null },
      lastDetail: { daily: null, weekly: null, monthly: null },
    };

    try {
      // 성능 측정(개발용): 전체 6개 API 병렬 호출
      let start = performance.now();

      // 5) 현재 기간 3종 + 비교 기간 3종을 병렬 호출
      //    - 동일 엔드포인트에 날짜만 바꿔서 호출
      const [
        baseDailyRes,
        baseWeeklyRes,
        baseMonthlyRes,
        lastDailyRes,
        lastWeeklyRes,
        lastMonthlyRes,
      ] = await Promise.all([
        // 현재 기간(일/주/월)
        mokiApi.get("/api/ranking/daily", { params: { localDate } }),
        mokiApi.get("/api/ranking/weekly", { params: { localDate } }),
        mokiApi.get("/api/ranking/monthly", { params: { localDate } }),
        // 비교 기간(일/주/월)
        mokiApi.get("/api/ranking/daily", {
          params: { localDate: formatDailyCompDate },
        }),
        mokiApi.get("/api/ranking/weekly", {
          params: { localDate: formatWeeklyCompDate },
        }),
        mokiApi.get("/api/ranking/monthly", {
          params: { localDate: formatMonthlyCompDate },
        }),
      ]);

      // 6) 서버 응답을 슬라이스 기대 스키마로 변환/취합
      //    - store_id는 일간 응답에서 대표로 사용(세 응답이 동일하다는 전제)
      resData = {
        date: localDate,
        store_id: baseDailyRes.data.store_id,
        rankDetail: {
          daily: baseDailyRes.data.data, // 현재 일간 랭킹 배열
          weekly: baseWeeklyRes.data.data, // 현재 주간 랭킹 배열
          monthly: baseMonthlyRes.data.data, // 현재 월간 랭킹 배열
        },
        lastDetail: {
          daily: lastDailyRes.data.data, // 비교(7일 전) 일간 랭킹 배열
          weekly: lastWeeklyRes.data.data, // 비교(28일 전) 주간 랭킹 배열
          monthly: lastMonthlyRes.data.data, // 비교(3개월 전) 월간 랭킹 배열
        },
      };

      let end = performance.now();
    } catch (e) {
      // 7) 네트워크/서버 에러: 현재는 로그만 출력하고 기본 resData 반환
      //    → extraReducers.rejected로도 떨어지므로 UI에서 에러 핸들링 가능
      console.log(e);
    }

    // 8) 최종 payload 반환(fulfilled/rejected 분기에 따라 리듀서 처리)
    return resData;
  },
);

// ranking 슬라이스: 상태/동기 리듀서/비동기 리듀서 정의
const rankingSlice = createSlice({
  name: "ranking",

  // 초기 상태: 오늘 날짜를 포맷팅하여 기본 date로 설정
  initialState: {
    date: formatDate(new Date()), // 현재 날짜(문자열)
    storeId: null, // 매장 ID
    rankDetail: { daily: [], weekly: [], monthly: [] }, // 현재 기간 랭킹
    lastDetail: { daily: [], weekly: [], monthly: [] }, // 비교 기간 랭킹
    isLoading: false, // 로딩 플래그
    error: null, // 에러 정보
  },

  reducers: {
    // 전체 랭킹 상태 초기화(로그아웃/스토어 리셋 시 사용)
    clearRanking: (state) => {
      state.date = null;
      state.storeId = null;
      state.rankDetail = { daily: [], weekly: [], monthly: [] };
      state.lastDetail = { daily: [], weekly: [], monthly: [] };
      state.isLoading = false;
      state.error = null;
    },
    // 기준 날짜 변경(문자열; formatDate로 변환된 값 권장)
    // - 변경 즉시 useRankingData 훅이 fetchRanking를 다시 디스패치함
    setDate: (state, action) => {
      state.date = action.payload;
    },
  },

  // 비동기(thunk) 상태 변화 처리
  extraReducers: (builder) =>
    builder
      // 요청 시작: 로딩 true, 에러 초기화
      .addCase(fetchRanking.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      // 요청 성공: payload로 상태 업데이트
      .addCase(fetchRanking.fulfilled, (state, { payload }) => {
        state.date = payload.date;
        state.storeId = payload.store_id;
        state.rankDetail = payload.rankDetail || {
          daily: [],
          weekly: [],
          monthly: [],
        };
        state.lastDetail = payload.lastDetail || {
          daily: [],
          weekly: [],
          monthly: [],
        };
        state.isLoading = false;
      })
      // 요청 실패: 로딩 false, 에러 저장
      .addCase(fetchRanking.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error; // { name, message, ... }
      }),
});

// 액션 크리에이터 export
export const { clearRanking, setDate } = rankingSlice.actions;

// 리듀서 기본 export
export default rankingSlice.reducer;
